/**
 * RetryQueueTab — Iter229
 *
 * Dashboard showing every failed/bounced/skipped send with:
 *   • Status buckets + volume + estimated cost preview
 *   • Retryable (has body) vs Blocked (no body archived) split
 *   • Per-row "Reenviar" (queue) or "Editar & Reenviar" (open drawer)
 *   • Bulk "Reenviar tudo agora" with cost preview modal
 *
 * Backend: GET /api/smith/outreach/retry-queue → { total, by_status,
 *          by_reason, retryable, blocked_no_body, blacklisted,
 *          estimated_cost_usd, items }
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertCircle, RotateCcw, Edit3, RefreshCw, Zap, Loader2,
         Ban, CheckCircle2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RetryQueueTab({ onRetrySingle, onEditRetry, onBulkResend, busy }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/smith/outreach/retry-queue?limit=500`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar retry queue");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doBulk = async () => {
    setConfirmOpen(false);
    if (onBulkResend) await onBulkResend();
    setTimeout(load, 3000);
  };

  if (loading) {
    return (
      <div className="rounded-2xl border p-8 flex items-center justify-center"
           style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}>
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <div className="rounded-2xl border p-10 text-center space-y-2"
           style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(16,185,129,0.30)" }}
           data-testid="retry-queue-empty">
        <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
        <div className="text-white font-black text-[15px]">Fila de retry limpa</div>
        <p className="text-[12px] text-slate-400">Nenhum e-mail falhou ainda — ótimo trabalho!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="retry-queue-tab">
      {/* Header + KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total falhos" value={data.total} color="#F87171" icon={AlertCircle} />
        <KpiCard label="Reenvávivel" value={data.retryable} color="#10B981" icon={RotateCcw}
          hint="Body arquivado disponível" />
        <KpiCard label="Sem body" value={data.blocked_no_body} color="#F59E0B" icon={Edit3}
          hint="Requer edição antes do retry" />
        <KpiCard label="Custo est." value={`$${(data.estimated_cost_usd || 0).toFixed(4)}`}
          color="#0EA5E9" icon={Zap} hint={`~$0.0004 × ${data.retryable} sends`} />
      </div>

      {/* Bulk action band + reasons */}
      <div className="rounded-xl border overflow-hidden"
           style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}>
        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3 flex-wrap">
          <button type="button" onClick={load}
            data-testid="retry-queue-refresh"
            className="h-8 px-2.5 rounded text-[11px] font-bold text-slate-300 border border-white/15 hover:border-white/30 inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <div className="flex-1 text-[12px] text-slate-300">
            {data.retryable > 0 && (
              <span>Reenviar todos os <b className="text-emerald-300">{data.retryable}</b> disponíveis por <b className="text-emerald-300">${(data.estimated_cost_usd || 0).toFixed(4)}</b></span>
            )}
            {data.blocked_no_body > 0 && (
              <span className="ml-2 text-amber-300">
                · {data.blocked_no_body} bloqueado(s) sem body — edite individualmente
              </span>
            )}
          </div>
          <button type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={busy || data.retryable === 0}
            data-testid="retry-queue-bulk-btn"
            className="h-8 px-3 rounded-lg text-[12px] font-black text-white inline-flex items-center gap-1.5 disabled:opacity-40"
            style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Reenviar tudo agora
          </button>
        </div>

        {/* Reasons breakdown */}
        {data.by_reason?.length > 0 && (
          <div className="px-4 py-2 border-b border-white/10 flex items-center gap-2 flex-wrap"
               style={{ background: "rgba(11,17,32,0.5)" }}>
            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Principais causas:</span>
            {data.by_reason.slice(0, 6).map((r, i) => (
              <span key={i} data-testid={`retry-queue-reason-${i}`}
                className="text-[10.5px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-slate-300"
                title={r.reason}>
                <b className="text-red-300">{r.n}×</b> {String(r.reason).slice(0, 30)}{r.reason?.length > 30 ? "…" : ""}
              </span>
            ))}
          </div>
        )}

        {/* Items table */}
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-[11.5px]" data-testid="retry-queue-table">
            <thead className="sticky top-0" style={{ background: "rgba(11,17,32,0.98)" }}>
              <tr className="text-slate-400 text-[10.5px] uppercase tracking-widest border-b border-white/10">
                <th className="px-4 py-2 text-left">Recipiente</th>
                <th className="px-4 py-2 text-left">Assunto</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Tentativas</th>
                <th className="px-4 py-2 text-left">Falhou em</th>
                <th className="px-4 py-2 text-left">Body</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it, i) => (
                <tr key={it.id} data-testid={`retry-queue-row-${i}`}
                    className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-4 py-2 text-white truncate max-w-[200px] font-mono text-[10.5px]">{it.to}</td>
                  <td className="px-4 py-2 text-slate-300 truncate max-w-[240px]">{it.subject || "—"}</td>
                  <td className="px-4 py-2">
                    <StatusChip status={it.status} />
                  </td>
                  <td className="px-4 py-2 tabular-nums text-slate-400">{it.attempts || 1}</td>
                  <td className="px-4 py-2 tabular-nums text-slate-500 text-[10.5px]">
                    {it.failed_at ? new Date(it.failed_at).toLocaleString("pt-BR",
                      { dateStyle: "short", timeStyle: "short" }) : "—"}
                  </td>
                  <td className="px-4 py-2">
                    {it.has_body
                      ? <span className="text-emerald-400" title="Body arquivado — reenvio direto">✓</span>
                      : <span className="text-amber-400" title="Body não arquivado — requer edição">⚠</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {it.has_body ? (
                      <button type="button" onClick={() => onRetrySingle(it.id, it.to)}
                        data-testid={`retry-queue-resend-${i}`}
                        title="Reenviar direto"
                        className="w-7 h-7 rounded flex items-center justify-center bg-emerald-500/15 hover:bg-emerald-500/35 text-emerald-300">
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <button type="button" onClick={() => onEditRetry(it)}
                        data-testid={`retry-queue-edit-${i}`}
                        title="Editar body & reenviar"
                        className="w-7 h-7 rounded flex items-center justify-center bg-amber-500/15 hover:bg-amber-500/35 text-amber-300">
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirmation modal for bulk resend */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
             data-testid="retry-queue-confirm-modal"
             style={{ background: "rgba(2,6,23,0.7)" }} onClick={() => setConfirmOpen(false)}>
          <div className="max-w-md w-full rounded-2xl border p-6 space-y-4"
               style={{ background: "#0B1120", borderColor: "rgba(220,38,38,0.30)" }}
               onClick={(e) => e.stopPropagation()}>
            <div className="text-white font-black text-[16px]">Confirmar reenvio em massa</div>
            <div className="space-y-1 text-[13px] text-slate-300">
              <div>Volume: <b className="text-white tabular-nums">{data.retryable}</b> e-mail(s)</div>
              <div>Custo estimado: <b className="text-emerald-300 tabular-nums">${(data.estimated_cost_usd || 0).toFixed(4)} USD</b></div>
              <div className="text-[11.5px] text-slate-400 mt-2">
                {data.blocked_no_body > 0 && (
                  <>⚠ {data.blocked_no_body} lead(s) sem body arquivado NÃO serão reenviados — edite individualmente.</>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setConfirmOpen(false)}
                className="h-9 px-3 rounded-lg text-[12px] font-bold text-slate-300 hover:bg-white/10">
                Cancelar
              </button>
              <button onClick={doBulk} disabled={busy}
                data-testid="retry-queue-confirm-btn"
                className="h-9 px-4 rounded-lg text-[12px] font-black text-white inline-flex items-center gap-1.5 disabled:opacity-40"
                style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                Confirmar & Reenviar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, color, icon: Icon, hint }) {
  return (
    <div className="rounded-xl border p-3"
         style={{ background: `${color}12`, borderColor: `${color}44` }}
         data-testid={`retry-kpi-${String(label).toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <div className="text-[10px] font-black uppercase tracking-widest" style={{ color }}>{label}</div>
      </div>
      <div className="text-[22px] font-black text-white tabular-nums">{value}</div>
      {hint && <div className="text-[9.5px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    failed:   { bg: "rgba(248,113,113,0.20)", fg: "#FCA5A5", icon: XCircle, label: "Failed" },
    bounced:  { bg: "rgba(245,158,11,0.20)",  fg: "#FCD34D", icon: XCircle, label: "Bounced" },
    skipped:  { bg: "rgba(148,163,184,0.20)", fg: "#CBD5E1", icon: Ban,    label: "Skipped" },
  };
  const s = map[status] || map.failed;
  const Icon = s.icon;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-widest"
          style={{ background: s.bg, color: s.fg }}>
      <Icon className="w-2.5 h-2.5" /> {s.label}
    </span>
  );
}
